/** Calculs financiers partagés (factures, avoirs, paiements, échéanciers). */
export const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

export const ttcToHt = (ttc: number, tvaPct: number) => round2(ttc / (1 + (tvaPct || 0) / 100));
export const htToTtc = (ht: number, tvaPct: number) => round2(ht * (1 + (tvaPct || 0) / 100));

export interface LigneCalc { quantite: number; prixUnitaireHT: number; tvaPct: number }

export function totauxDocument(lignes: LigneCalc[], remisePct = 0) {
  const k = 1 - (remisePct || 0) / 100;
  const parTaux = new Map<number, { base: number; tva: number }>();
  let totalHT = 0;
  for (const l of lignes) {
    const ht = (l.quantite || 0) * (l.prixUnitaireHT || 0);
    totalHT += ht;
    const net = ht * k;
    const cur = parTaux.get(l.tvaPct || 0) || { base: 0, tva: 0 };
    cur.base += net; cur.tva += net * ((l.tvaPct || 0) / 100);
    parTaux.set(l.tvaPct || 0, cur);
  }
  const htNet = totalHT * k;
  const tva = [...parTaux.values()].reduce((s, t) => s + t.tva, 0);
  return {
    totalHT: round2(totalHT),
    remise: round2(totalHT - htNet),
    htNet: round2(htNet),
    tvaParTaux: [...parTaux.entries()].sort((a, b) => a[0] - b[0]).map(([taux, t]) => ({ taux, base: round2(t.base), tva: round2(t.tva) })),
    tva: round2(tva),
    ttc: round2(htNet + tva),
  };
}

/** Reste à payer = TTC − acomptes déduits − paiements encaissés − avoirs émis. */
export function resteAPayer(ttc: number, opts: { acomptes?: number; paiements?: number[]; avoirsTTC?: number[] } = {}) {
  const paye = (opts.paiements || []).reduce((s, x) => s + (x || 0), 0);
  const av = (opts.avoirsTTC || []).reduce((s, x) => s + Math.abs(x || 0), 0);
  return round2(ttc - (opts.acomptes || 0) - paye - av);
}

/** Statut de paiement déduit des encaissements. */
export function statutPaiement(ttcDu: number, encaisse: number, echeance: string | null, today = new Date().toISOString().slice(0, 10)): 'Payée' | 'Partiellement payée' | 'En retard' | null {
  if (ttcDu > 0 && encaisse >= ttcDu - 0.005) return 'Payée';
  if (echeance && echeance < today) return 'En retard';
  if (encaisse > 0) return 'Partiellement payée';
  return null;
}

/** Contrôle avant finalisation : renvoie la liste des informations manquantes. */
export function controleFinalisation(f: {
  clientNom?: string; clientAdresse?: string; numero?: string; dateEmission?: string; dateEcheance?: string;
  conditionsPaiement?: string; lignes: (LigneCalc & { description?: string; typeLigne?: string | null })[];
}): string[] {
  const m: string[] = [];
  if (!f.clientNom?.trim()) m.push('Client');
  if (!f.clientAdresse?.trim()) m.push('Adresse du client');
  if (!f.numero?.trim()) m.push('Numéro du document');
  if (!f.dateEmission) m.push("Date d'émission");
  if (!f.dateEcheance) m.push("Date d'échéance");
  if (!f.conditionsPaiement?.trim()) m.push('Conditions de règlement');
  if (!f.lignes.some((l) => estChiffree(l.typeLigne))) m.push('Au moins une ligne');
  f.lignes.forEach((l, i) => {
    if (!estChiffree(l.typeLigne)) return;
    if (!l.description?.trim()) m.push(`Ligne ${i + 1} : désignation`);
    if (!l.quantite) m.push(`Ligne ${i + 1} : quantité`);
    if (l.tvaPct == null || isNaN(l.tvaPct)) m.push(`Ligne ${i + 1} : TVA`);
  });
  return m;
}

/** Vérifie qu'un échéancier correspond au total à payer (tolérance 1 centime). */
export const echeancierEquilibre = (montants: number[], total: number) =>
  Math.abs(round2(montants.reduce((s, x) => s + (x || 0), 0)) - round2(total)) < 0.01;

/** Prix unitaire net HT après remise ligne (%). */
export const prixNetHT = (prixBrutHT: number, remisePct = 0) =>
  round2((prixBrutHT || 0) * (1 - Math.max(0, Math.min(100, remisePct || 0)) / 100));

/** Convertit un prix saisi en HT ou TTC vers un prix HT. */
export const prixSaisiVersHT = (valeur: number, base: 'HT' | 'TTC', tvaPct: number) =>
  base === 'TTC' ? ttcToHt(valeur, tvaPct) : round2(valeur);

/** Lignes non chiffrées (titre, commentaire, saut de page) : exclues des totaux. */
export const LIGNES_NON_CHIFFREES = ['section', 'commentaire', 'saut_page'] as const;
export const estChiffree = (type?: string | null) => !type || !(LIGNES_NON_CHIFFREES as readonly string[]).includes(type);

/** Recalcule la dernière échéance pour que le total corresponde exactement. */
export function repartirSolde(montants: number[], total: number): number[] {
  if (!montants.length) return [round2(total)];
  const avant = montants.slice(0, -1).reduce((s, x) => s + (x || 0), 0);
  return [...montants.slice(0, -1), round2(total - avant)];
}

/** Montant restant à facturer sur une proposition (solde). */
export const resteAFacturer = (totalTTC: number, dejaFactureTTC: number[]) =>
  round2(totalTTC - dejaFactureTTC.reduce((s, x) => s + (x || 0), 0));
